/**
 * Een afdruk omzetten naar ESC/POS, de taal die vrijwel elke bonprinter spreekt.
 *
 * Dit is bewust een aparte laag. De opmaak van de bon staat in bonafdruk.ts en
 * weet niets van printers; hier wordt daar een reeks bytes van gemaakt. Blijkt
 * het toestel straks een eigen SDK te hebben in plaats van ESC/POS, dan
 * vervangt dat alleen dit bestand.
 *
 * Tekens gaan als CP437 de deur uit — dat is wat de meeste bonprinters
 * verstaan. Accenten die daar niet in passen worden vervangen door hun gewone
 * letter: liever "Cafe" dan een blokje.
 */

import type { Afdruk, Afdrukregel } from './bonafdruk.js';

const ESC = 0x1b;
const GS = 0x1d;
const LF = 0x0a;

/** Vervangt tekens die een bonprinter niet kent door iets leesbaars. */
export function naarPrinterTekens(tekst: string): string {
  return tekst
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[’‘]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/[–—]/g, '-')
    .replace(/€/g, 'EUR ')
    .replace(/[^\x20-\x7e]/g, ' ');
}

function bytesVanTekst(tekst: string): number[] {
  return [...naarPrinterTekens(tekst)].map((teken) => teken.charCodeAt(0) & 0x7f);
}

function grootteBytes(regel: Afdrukregel): number[] {
  // GS ! n — de vier hoge bits zijn de breedte, de vier lage de hoogte.
  const n = regel.grootte === 'zeergroot' ? 0x22 : regel.grootte === 'groot' ? 0x11 : 0x00;
  return [GS, 0x21, n];
}

function uitlijnBytes(regel: Afdrukregel): number[] {
  const n = regel.uitlijning === 'midden' ? 1 : regel.uitlijning === 'rechts' ? 2 : 0;
  return [ESC, 0x61, n];
}

export interface EscposInstellingen {
  /** Hoeveel regels er na de bon worden doorgevoerd, zodat je hem kunt afscheuren. */
  regelsNa?: number;
  /** Automatisch afsnijden, als de printer een mes heeft. */
  afsnijden?: boolean;
}

/**
 * Zet een afdruk om in bytes voor de printer.
 *
 * Regels met een bedrag rechts worden hier opgevuld met spaties in plaats van
 * met een tabstop: tabstops verschillen per printer, spaties niet.
 */
export function naarEscpos(afdruk: Afdruk, instellingen: EscposInstellingen = {}): Uint8Array {
  const bytes: number[] = [
    ESC,
    0x40, // printer terugzetten op zijn beginstand
    ESC,
    0x74,
    0x00, // tekentabel CP437
  ];

  for (const regel of afdruk.regels) {
    if (regel.leeg === true) {
      bytes.push(LF);
      continue;
    }

    bytes.push(...uitlijnBytes(regel), ...grootteBytes(regel));
    bytes.push(ESC, 0x45, regel.vet === true ? 1 : 0);

    if (regel.streep === true) {
      bytes.push(...bytesVanTekst('-'.repeat(afdruk.breedte)));
    } else if (regel.rechts != null && regel.rechts !== '') {
      const beschikbaar =
        regel.grootte === 'zeergroot'
          ? Math.floor(afdruk.breedte / 3)
          : regel.grootte === 'groot'
            ? Math.floor(afdruk.breedte / 2)
            : afdruk.breedte;
      const links = naarPrinterTekens(regel.tekst);
      const rechts = naarPrinterTekens(regel.rechts);
      const ruimte = Math.max(1, beschikbaar - links.length - rechts.length);
      bytes.push(...bytesVanTekst(`${links}${' '.repeat(ruimte)}${rechts}`));
    } else {
      bytes.push(...bytesVanTekst(regel.tekst));
    }

    bytes.push(ESC, 0x45, 0, LF);
  }

  for (let i = 0; i < (instellingen.regelsNa ?? 4); i += 1) bytes.push(LF);
  if (instellingen.afsnijden !== false) bytes.push(GS, 0x56, 0x42, 0x00);

  return Uint8Array.from(bytes);
}

/** De opdracht die vraagt of de printer er nog is en papier heeft. */
export const STATUSOPDRACHT = Uint8Array.from([0x10, 0x04, 0x01]);
